import { gql, useLazyQuery } from "@apollo/client";
import { useCallback, useEffect } from "react";

const GET_DESIGNER_ONBOARDING_STATUS = gql`
  query GetDesignerOnboardingStatus($designerId: ID!) {
    getDesignerOnboardingStatus(designerId: $designerId) {
      bioComplete
      designFilesComplete
      matchingProfileComplete
    }
  }
`;

export const useGetDesignerOnboardingStatusQuery = (designerId?: string) => {
  const [getDesignerOnboardingStatusQuery, { loading, data, refetch, error, called }] =
    useLazyQuery(GET_DESIGNER_ONBOARDING_STATUS, {
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true
    });
  
  // Debug logging
  console.log('🔍 useGetDesignerOnboardingStatusQuery: data:', data, 'loading:', loading, 'error:', error);

  const getDesignerOnboardingStatus = useCallback(
    async (designerId: string, refresh: boolean = false) => {
      const result = await getDesignerOnboardingStatusQuery({
        variables: {
          designerId
        }
      });
      return result?.data?.getDesignerOnboardingStatus;
    },
    [getDesignerOnboardingStatusQuery]
  );
  
  const refreshOnboardingStatus = useCallback(async (designerId?: string) => {
    if (designerId) {
      // Use the original query for refresh
      const result = await getDesignerOnboardingStatusQuery({
        variables: { designerId }
      });
      return result?.data?.getDesignerOnboardingStatus;
    } else if (refetch) {
      // Fallback to refetch if no designerId provided
      const result = await refetch();
      return result?.data?.getDesignerOnboardingStatus;
    }
  }, [refetch, getDesignerOnboardingStatusQuery]);

  const onboardingStatus = data?.getDesignerOnboardingStatus;
  
  // Debug the extracted status
  console.log('🔍 useGetDesignerOnboardingStatusQuery - extracted onboardingStatus:', onboardingStatus);

  // Auto-fetch when designerId is provided and we haven't fired yet
  useEffect(() => {
    if (designerId && !called) {
      getDesignerOnboardingStatusQuery({ variables: { designerId } });
    }
  }, [designerId, called, getDesignerOnboardingStatusQuery]);

  // Consider the hook "loading" during the initial auto-fetch, even before Apollo sets loading true
  const derivedLoading = loading || (designerId ? !called && !data && !error : false);

  return {
    getDesignerOnboardingStatus,
    refreshOnboardingStatus,
    loading: derivedLoading,
    onboardingStatus,
  };
};
